import type { MatrixCodeRevision, MatrixFunctionalInspectionType } from '@/entities/Matrix';
import { DocumentRevisionStatus } from '@/shared/lib';

/** `identify` opens the device modal before the generate screen; `enabled` goes straight to it. */
export type MatrixRevisionGenerateAction = 'enabled' | 'identify' | 'disabled' | 'hidden';

const GENERATING_STATUSES: readonly DocumentRevisionStatus[] = [
  DocumentRevisionStatus.CURRENT,
  DocumentRevisionStatus.OBSOLETE,
];

// Matched by name because record type ids are per branch; the server refuses the same names.
const GENERATING_RECORD_TYPE_NAMES: readonly string[] = ['Editável', 'Upload'];
const INSPECTION_RECORD_TYPE_NAME = 'Editável (IF/API)';
const EXTERNAL_SOFTWARE_RECORD_TYPE_NAME = 'Software externo';

type GenerateActionRevision = Pick<
  MatrixCodeRevision,
  'status' | 'recordTypeName' | 'functionalInspectionType'
>;

/** The IF or OS subtype the device modal asks about, or null when the revision needs no modal. */
export function resolveMatrixRevisionInspectionType(
  revision: Pick<MatrixCodeRevision, 'recordTypeName' | 'functionalInspectionType'>
): MatrixFunctionalInspectionType | null {
  return revision.recordTypeName === INSPECTION_RECORD_TYPE_NAME
    ? revision.functionalInspectionType
    : null;
}

/**
 * A revision the rules forbid hides the icon; a type whose flow is not built yet keeps it disabled
 * and without a hint, so the product can tell "never" from "not yet".
 */
export function resolveMatrixRevisionGenerateAction(
  revision: GenerateActionRevision
): MatrixRevisionGenerateAction {
  if (
    !GENERATING_STATUSES.includes(revision.status) ||
    revision.recordTypeName === EXTERNAL_SOFTWARE_RECORD_TYPE_NAME
  ) {
    return 'hidden';
  }

  if (resolveMatrixRevisionInspectionType(revision) !== null) {
    return 'identify';
  }

  return GENERATING_RECORD_TYPE_NAMES.includes(revision.recordTypeName ?? '') ? 'enabled' : 'disabled';
}
